import { useEffect, useRef, useState } from "react";
import api, { streamPost } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Icon } from "@/lib/icons";
import { Markdown } from "@/lib/markdown";
import { LockedFeature } from "@/components/LockedFeature";
import { ProgressRing } from "@/components/ProgressRing";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Send, Loader2, ArrowLeft, Trophy, History, CheckCircle2, AlertTriangle, Lightbulb, MessageSquareQuote } from "lucide-react";

const DIFFICULTIES = ["beginner", "intermediate", "advanced", "expert"];
function newId() { return (crypto.randomUUID && crypto.randomUUID()) || `n_${Date.now()}`; }

export default function Negotiation() {
  const { user } = useAuth();
  const isPro = user?.is_pro || user?.tier === "pro";
  const [scenarios, setScenarios] = useState([]);
  const [active, setActive] = useState(null);
  const [difficulty, setDifficulty] = useState("intermediate");
  const [sessionId, setSessionId] = useState(newId());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [evaluation, setEvaluation] = useState(null);
  const [evalOpen, setEvalOpen] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { api.get("/negotiation/scenarios").then((r) => setScenarios(r.data)).catch(() => {}); }, []);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages]);

  const loadSessions = () => api.get("/negotiation/sessions").then((r) => setSessions(r.data)).catch(() => {});

  if (!isPro) return (
    <LockedFeature title="Negotiation Masterclass" description="Practice 8 negotiation types against a realistic AI counterpart across 4 difficulty levels, then get a performance score with strengths, weaknesses and exactly what to say next time."
      perks={["8 scenarios · Beginner → Expert difficulty", "Performance score after each session", "Strengths, weaknesses & missed opportunities", "Suggested responses to win the deal"]} />
  );

  const start = (sc) => { setActive(sc); setSessionId(newId()); setMessages([]); setEvaluation(null); };

  const resume = async (s) => {
    const sc = scenarios.find((x) => x.id === s.scenario_id) || { id: s.scenario_id, title: s.title, icon: s.icon, desc: "" };
    setActive(sc); setSessionId(s.session_id); setEvaluation(null);
    const { data } = await api.get(`/negotiation/messages?session_id=${s.session_id}`);
    setMessages(data);
  };

  const send = async () => {
    const content = input.trim();
    if (!content || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      await streamPost("/negotiation/chat", { scenario_id: active.id, session_id: sessionId, message: content, difficulty }, (chunk) => {
        setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + chunk }; return c; });
      });
    } catch (e) { toast.error(e.message || "Error"); setMessages((m) => m.slice(0, -1)); }
    finally { setStreaming(false); }
  };

  const evaluate = async () => {
    if (messages.length < 2) { toast.error("Negotiate a bit first, then get your score."); return; }
    setEvaluating(true); setEvalOpen(true); setEvaluation(null);
    try {
      const { data } = await api.post("/negotiation/evaluate", { session_id: sessionId, scenario_id: active.id });
      setEvaluation(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not evaluate"); setEvalOpen(false); }
    finally { setEvaluating(false); }
  };

  // ----- Scenario selection -----
  if (!active) {
    return (
      <div className="space-y-8" data-testid="negotiation-page">
        <div className="flex items-start justify-between gap-3">
          <div>
            <span className="font-mono-eco text-xs uppercase tracking-widest text-slate-500">Negotiation Masterclass</span>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight mt-1">Train against a real opponent.</h1>
            <p className="text-slate-400 mt-2">Pick a scenario and difficulty. ECO plays the other party — then scores your performance.</p>
          </div>
          <Sheet onOpenChange={(o) => o && loadSessions()}>
            <SheetTrigger asChild><button data-testid="neg-history-btn" className="btn-secondary text-sm py-2 px-3 shrink-0"><History size={16} /></button></SheetTrigger>
            <SheetContent side="right" className="bg-[#0A0A0A] border-white/10 text-white w-[330px]">
              <SheetHeader><SheetTitle className="text-white font-display">Past negotiations</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-2 overflow-y-auto max-h-[calc(100vh-160px)] no-scrollbar">
                {sessions.length === 0 && <p className="text-sm text-slate-500 text-center py-8">No negotiations yet.</p>}
                {sessions.map((s) => (
                  <button key={s.session_id} onClick={() => resume(s)} data-testid={`neg-session-${s.session_id}`} className="w-full text-left p-3 rounded-xl border border-white/5 hover:bg-white/5 flex items-center gap-2">
                    <Icon name={s.icon} size={16} className="text-[#4d8bff] shrink-0" />
                    <span className="text-sm text-slate-300 flex-1">{s.title}</span>
                    {s.score != null && <span className="eco-tag text-emerald-300 border-emerald-400/20 bg-emerald-400/10">{s.score}</span>}
                  </button>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>

        <div>
          <div className="font-mono-eco text-[10px] uppercase tracking-widest text-slate-500 mb-2">Difficulty</div>
          <div className="inline-flex items-center bg-white/5 border border-white/10 rounded-full p-1 flex-wrap">
            {DIFFICULTIES.map((d) => (
              <button key={d} data-testid={`difficulty-${d}`} onClick={() => setDifficulty(d)} className={`text-xs font-medium px-3 py-1.5 rounded-full capitalize transition-all ${difficulty === d ? "bg-white text-black" : "text-slate-400"}`}>{d}</button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {scenarios.map((s) => (
            <button key={s.id} data-testid={`scenario-${s.id}`} onClick={() => start(s)} className="eco-card eco-card-hover p-6 text-left">
              <div className="h-11 w-11 rounded-xl bg-[#0055FF]/15 border border-[#0055FF]/30 flex items-center justify-center mb-4"><Icon name={s.icon} size={22} className="text-[#4d8bff]" /></div>
              <h3 className="font-display text-lg font-semibold text-white">{s.title}</h3>
              <p className="text-sm text-slate-400 mt-1.5">{s.desc}</p>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ----- Chat -----
  return (
    <div className="flex flex-col h-[calc(100vh-160px)] md:h-[calc(100vh-130px)]" data-testid="negotiation-chat">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => setActive(null)} className="btn-secondary text-sm py-2 px-3"><ArrowLeft size={16} /></button>
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="h-9 w-9 rounded-xl bg-[#0055FF]/15 border border-[#0055FF]/30 flex items-center justify-center shrink-0"><Icon name={active.icon} size={18} className="text-[#4d8bff]" /></div>
          <div className="min-w-0">
            <h1 className="font-display font-semibold text-white leading-none truncate">{active.title}</h1>
            <p className="text-[11px] text-slate-500 mt-0.5 capitalize">{difficulty} difficulty</p>
          </div>
        </div>
        <div className="ml-auto flex gap-2">
          <button onClick={evaluate} data-testid="neg-evaluate-btn" className="btn-accent text-sm py-2 px-3"><Trophy size={15} /> Get score</button>
          <button onClick={() => start(active)} className="btn-secondary text-sm py-2 px-3">Restart</button>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto no-scrollbar space-y-4 pr-1">
        {messages.length === 0 && (
          <div className="eco-card p-6 max-w-lg"><p className="text-slate-300 text-sm">{active.desc} Open with your first offer or question — ECO responds in character. When you're done, hit <strong>Get score</strong>.</p></div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={m.role === "user" ? "bg-white/10 text-white rounded-2xl rounded-tr-sm px-4 py-3 max-w-[85%]" : "bg-transparent border border-white/5 text-slate-300 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[88%]"}>
              {m.content || <span className="stream-caret text-slate-500" />}
              {streaming && i === messages.length - 1 && m.content && <span className="stream-caret" />}
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-end gap-2 mt-4">
        <textarea data-testid="negotiation-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} rows={1} placeholder="Your move…" className="flex-1 resize-none bg-[#0A0A0A] border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-white/25 max-h-32" />
        <button type="submit" data-testid="negotiation-send" disabled={streaming || !input.trim()} className="btn-accent h-12 w-12 !px-0 rounded-2xl shrink-0">{streaming ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}</button>
      </form>

      <Dialog open={evalOpen} onOpenChange={setEvalOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="neg-eval-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl flex items-center gap-2"><Trophy size={20} className="text-amber-400" /> Negotiation report</DialogTitle></DialogHeader>
          {evaluating ? (
            <div className="flex flex-col items-center py-16 gap-3"><Loader2 className="animate-spin text-[#4d8bff]" /><p className="text-sm text-slate-500">Your coach is analyzing the deal…</p></div>
          ) : evaluation ? (
            <div className="space-y-5">
              <div className="flex items-center gap-5">
                <ProgressRing value={evaluation.score} size={88} color="#10B981" />
                <p className="text-slate-300 text-sm flex-1">{evaluation.summary}</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <EvalList icon={CheckCircle2} title="Strengths" items={evaluation.strengths} color="#10B981" />
                <EvalList icon={AlertTriangle} title="Weaknesses" items={evaluation.weaknesses} color="#EF4444" />
              </div>
              <EvalList icon={Lightbulb} title="Missed opportunities" items={evaluation.missed_opportunities} color="#F59E0B" />
              {evaluation.suggested_responses?.length > 0 && (
                <div className="eco-card p-5">
                  <h4 className="flex items-center gap-2 text-sm font-semibold text-white mb-3"><MessageSquareQuote size={16} className="text-[#4d8bff]" /> What to say next time</h4>
                  <div className="space-y-3">
                    {evaluation.suggested_responses.map((s, i) => (
                      <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-3">
                        <div className="text-xs text-slate-500">{s.situation}</div>
                        <div className="text-sm text-white mt-1">“{s.better_response}”</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {evaluation.analysis && <div className="eco-card p-5"><Markdown>{evaluation.analysis}</Markdown></div>}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EvalList({ icon: I, title, items, color }) {
  if (!items?.length) return null;
  return (
    <div className="eco-card p-5">
      <h4 className="flex items-center gap-2 text-sm font-semibold text-white mb-3"><I size={16} style={{ color }} /> {title}</h4>
      <ul className="space-y-2">{items.map((x, i) => <li key={i} className="flex items-start gap-2.5 text-sm text-slate-300"><span className="h-1.5 w-1.5 rounded-full mt-2 shrink-0" style={{ background: color }} /> {x}</li>)}</ul>
    </div>
  );
}
